export const TIERS = {
  commis: {
    id: 'commis',
    name: 'Commis',
    tagline: 'The Apprentice',
    price: 0,
    features: [
      'Inventory management',
      'Recipe catalog',
      'Shopping list',
      'Chef\'s Journal',
      'Recipe AI (3/mo)',
    ],
    limits: { recipeAI: 3, overloadAI: 0 },
    routes: ['/', '/inventory', '/recipes', '/shopping-list', '/ai-recipe'],
  },
  sous: {
    id: 'sous',
    name: 'Sous',
    tagline: 'Full Kitchen',
    price: 5,
    features: [
      'Everything in Commis',
      'Meal planner',
      'Macro tracking',
      'Budget planner',
      'Meal Synthesizer',
      'Recipe AI (30/mo)',
    ],
    limits: { recipeAI: 30, overloadAI: 0 },
    routes: [
      '/', '/inventory', '/recipes', '/shopping-list',
      '/meal-planner', '/macro-planner', '/budget', '/meal-synthesizer',
      '/ai-recipe',
    ],
  },
  coach: {
    id: 'coach',
    name: 'Coach',
    tagline: 'Full Protocol',
    price: 5,
    features: [
      'Everything in Commis',
      'Biometric profiling',
      'Workout protocol generator',
      'Protocol calendar',
      'Progressive Overload AI (8/mo)',
      'Fitness log',
    ],
    limits: { recipeAI: 3, overloadAI: 8 },
    routes: [
      '/', '/inventory', '/recipes', '/shopping-list',
      '/biometrics', '/workout-protocol', '/calendar',
      '/progressive-overload', '/fitness', '/ai-recipe',
    ],
  },
  chef: {
    id: 'chef',
    name: 'Chef de Partie',
    tagline: 'The Full Station',
    price: 9,
    features: [
      'Everything in Sous + Coach',
      'Recipe AI (100/mo)',
      'Sonnet-powered Overload AI (30/mo)',
      'Priority cloud sync',
    ],
    limits: { recipeAI: 100, overloadAI: 30 },
    routes: 'all',
  },
};

export const TIER_ORDER = ['commis', 'sous', 'coach', 'chef'];

export const DEFAULT_TIER = 'commis';

const ALWAYS_ALLOWED = ['/settings', '/upgrade', '/feedback', '/brigade', '/blog', '/catalog', '/admin', '/community', '/discover', '/profile'];

export const isRouteAllowed = (tier, path) => {
  const tierConfig = TIERS[tier] || TIERS[DEFAULT_TIER];
  if (tierConfig.routes === 'all') return true;
  if (ALWAYS_ALLOWED.some((p) => path === p || path.startsWith(p + '/'))) return true;
  return tierConfig.routes.some((r) =>
    r === path || (r === '/' ? path === '/' : path.startsWith(r))
  );
};

export const getAILimit = (tier, feature) => {
  const tierConfig = TIERS[tier] || TIERS[DEFAULT_TIER];
  return tierConfig.limits[feature] || 0;
};

export const usesSonnet = (tier) => tier === 'chef';

// Chef-rank-aware empty state copy. Adds subtle warmth based on tier.
const RANK_TONES = {
  commis: {
    journalEmpty: 'A chef\'s journal starts with one note.',
    inventoryEmpty: 'Every well-stocked station starts empty.',
    recipesEmpty: 'Your first recipe sets the tone.',
    shoppingEmpty: 'A clean list. The kitchen breathes.',
    plannerEmpty: 'A blank week — plan your service.',
    feedEmpty: 'Welcome, Commis. The brigade awaits your first dish.',
  },
  sous: {
    journalEmpty: 'Captain the line — log a thought.',
    inventoryEmpty: 'Stock the station. Mise en place.',
    recipesEmpty: 'Codify what you cook well.',
    shoppingEmpty: 'No prep order yet — you\'re ahead.',
    plannerEmpty: 'Lay out the week, Sous.',
    feedEmpty: 'The brigade is quiet today, Sous.',
  },
  coach: {
    journalEmpty: 'A coach reflects to refine.',
    inventoryEmpty: 'Fuel the work — stock up.',
    recipesEmpty: 'Programs need recipes too.',
    shoppingEmpty: 'Empty list, full station.',
    plannerEmpty: 'Plan the week\'s training nutrition.',
    feedEmpty: 'No new dispatches from the line.',
  },
  chef: {
    journalEmpty: 'Even a Chef de Partie keeps notes.',
    inventoryEmpty: 'A clean larder — start fresh.',
    recipesEmpty: 'Your library awaits its first entry.',
    shoppingEmpty: 'A clear list. The mark of preparation.',
    plannerEmpty: 'Compose the week, Chef.',
    feedEmpty: 'The brigade is between services.',
  },
};

export const getRankTone = (tier, key) => {
  const t = (tier && RANK_TONES[tier]) ? tier : 'commis';
  return RANK_TONES[t][key] || RANK_TONES.commis[key] || '';
};

